import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTonAddress, useTonConnectUI } from "@tonconnect/ui-react";
import { useParams } from "react-router-dom";
import { Address } from "ton-core";
import { useSendFBAnalyticsEvent } from "../hooks/analytics";
import { Dao } from "../wrappers/Dao";
import { JettonWallet } from "../wrappers/JettonWallet";
import { NftProposal, NftProposalData } from "../wrappers/NftProposal";
import { getTonClient } from "./tonClient";
import { getSeqno, waitWalletUpdate } from "./walletService";

export const useProposalData = (proposalId: bigint) => {
  const { address } = useParams();

  return useQuery(
    [address, "proposal", proposalId.toString()],
    async () => {
      const tonClient = await getTonClient();
      const dao = tonClient.open(
        Dao.createFromAddress(Address.parse(address!))
      );

      const proposalAddress = await dao.getNftProposalAddress(proposalId);

      const proposal = tonClient.open(
        NftProposal.createFromAddress(proposalAddress)
      );

      return await proposal.getData();
    },
    { enabled: address != null }
  );
};

export const useExecuteMutation = (proposal: NftProposalData) => {
  const { address } = useParams();
  const [tonConnectUi] = useTonConnectUI();
  const owner = useTonAddress(false);
  const client = useQueryClient();
  const sendFBAnalyticsEvent = useSendFBAnalyticsEvent();

  return useMutation<void, Error>(async () => {
    sendFBAnalyticsEvent("execute_proposal");
    const tonClient = await getTonClient();
    const dao = tonClient.open(Dao.createFromAddress(Address.parse(address!)));
    const proposalAddress = await dao.getNftProposalAddress(proposal.votingId);

    const walletSeqno = await getSeqno(owner);

    await tonConnectUi.sendTransaction({
      validUntil: Date.now() + 1000000,
      messages: [
        {
          address: proposalAddress.toRawString(),
          amount: "100000000", // 0.1 TON
          payload: NftProposal.executeMessage().toBoc().toString("base64"),
        },
      ],
    });

    await waitWalletUpdate(owner, walletSeqno);

    await client.invalidateQueries([
      address,
      "proposal",
      proposal.votingId.toString(),
    ]);
  });
};

export const useVoteMutation = (proposal: NftProposalData) => {
  const { address } = useParams();
  const owner = useTonAddress(false);
  const [tonConnectUi] = useTonConnectUI();

  const client = useQueryClient();
  const sendFBAnalyticsEvent = useSendFBAnalyticsEvent();

  return useMutation<void, Error, boolean>(async (vote: boolean) => {
    sendFBAnalyticsEvent("vote_proposal");
    const tonClient = await getTonClient();
    const dao = tonClient.open(Dao.createFromAddress(Address.parse(address!)));
    const jettonAddress = await dao.getWalletAddress(Address.parse(owner));

    const deployed = await tonClient.isContractDeployed(jettonAddress);
    if (!deployed) {
      throw new Error("Jetton wallet is not deployed");
    }
    const proposalAddress = await dao.getNftProposalAddress(proposal.votingId);

    const walletSeqno = await getSeqno(owner);

    await tonConnectUi.sendTransaction({
      validUntil: Date.now() + 1000000,
      messages: [
        {
          address: jettonAddress.toRawString(),
          amount: "100000000", // 0.1 TON
          payload: JettonWallet.voteMessage(
            proposalAddress,
            proposal.expirationDate,
            vote
          )
            .toBoc()
            .toString("base64"),
        },
      ],
    });

    await waitWalletUpdate(owner, walletSeqno);

    await client.invalidateQueries([
      address,
      "proposal",
      proposal.votingId.toString(),
    ]);
  });
};
